import React, {
  useState,
} from "react";

import {
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaFilePdf,
  FaImage,
  FaLink,
  FaPlus,
  FaTrashAlt,
  FaVideo,
  FaYoutube,
} from "react-icons/fa";

import {
  resolveAdminTutorialMediaUrl,
  truncateMediaUrl,
} from "../utils/tutorialAdminMediaUtils";

function sortByOrder(items = []) {
  return [...items].sort(
    (a, b) =>
      Number(a.ordre || 0) -
      Number(b.ordre || 0)
  );
}

const MEDIA_CONFIG = {
  image: {
    label: "Image",
    icon: <FaImage />,
    color:
      "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
  },

  youtube: {
    label: "YouTube",
    icon: <FaYoutube />,
    color:
      "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  },

  video: {
    label: "Vidéo",
    icon: <FaVideo />,
    color:
      "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  },

  pdf: {
    label: "PDF",
    icon: <FaFilePdf />,
    color:
      "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  },

  lien: {
    label: "Lien",
    icon: <FaLink />,
    color:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  },
};

export default function TutorialMediaList({
  medias = [],
  busy,
  onCreate,
  onEdit,
  onDelete,
  onToggleActive,
}) {
  const sortedMedias =
    sortByOrder(medias);

  return (
    <section className="mt-5 overflow-hidden rounded-[20px] border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-950/40">
      <header className="flex flex-col gap-3 border-b border-slate-200 p-4 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h5 className="font-semibold text-slate-900 dark:text-white">
            Médias de l’étape
          </h5>

          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Images, vidéos, documents
            PDF et liens complémentaires.
          </p>
        </div>

        <button
          type="button"
          disabled={busy}
          onClick={onCreate}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-50"
        >
          <FaPlus />
          Ajouter un média
        </button>
      </header>

      {sortedMedias.length ? (
        <div className="grid gap-3 p-4 xl:grid-cols-2">
          {sortedMedias.map(
            (media) => (
              <MediaCard
                key={media.id}
                media={media}
                busy={busy}
                onEdit={() =>
                  onEdit(media)
                }
                onDelete={() =>
                  onDelete(media)
                }
                onToggleActive={() =>
                  onToggleActive(media)
                }
              />
            )
          )}
        </div>
      ) : (
        <div className="p-7 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-xl text-slate-400 shadow-sm dark:bg-slate-900">
            <FaImage />
          </div>

          <div className="mt-3 font-semibold text-slate-900 dark:text-white">
            Aucun média
          </div>

          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Ajoute une image ou une
            ressource à cette étape.
          </p>

          <button
            type="button"
            disabled={busy}
            onClick={onCreate}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            <FaPlus />
            Ajouter un média
          </button>
        </div>
      )}
    </section>
  );
}

function MediaCard({
  media,
  busy,
  onEdit,
  onDelete,
  onToggleActive,
}) {
  const config =
    MEDIA_CONFIG[
      media.type_media
    ] || MEDIA_CONFIG.lien;

  const active =
    media.is_active !== false;

  return (
    <article
      className={`overflow-hidden rounded-[18px] border bg-white transition dark:bg-slate-900 ${
        active
          ? "border-slate-200 dark:border-slate-700"
          : "border-slate-200 opacity-60 dark:border-slate-800"
      }`}
    >
      <div className="flex gap-4 p-4">
        <MediaPreview media={media} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold ${config.color}`}
            >
              {config.icon}
              {config.label}
            </span>

            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              ordre {media.ordre}
            </span>

            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                active
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                  : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              {active
                ? "Actif"
                : "Inactif"}
            </span>
          </div>

          <div className="mt-3 font-semibold text-slate-900 dark:text-white">
            {media.alt_text ||
              config.label}
          </div>

          <div
            className="mt-1 break-all text-xs leading-5 text-slate-400"
            title={media.url}
          >
            {truncateMediaUrl(
              media.url,
              100
            )}
          </div>

          {media.legende ? (
            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              {media.legende}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 px-4 py-3 dark:border-slate-800">
        <button
          type="button"
          disabled={busy}
          onClick={onToggleActive}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          {active ? (
            <FaEyeSlash />
          ) : (
            <FaEye />
          )}

          {active
            ? "Désactiver"
            : "Activer"}
        </button>

        <button
          type="button"
          disabled={busy}
          onClick={onEdit}
          className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-medium text-violet-700 transition hover:bg-violet-100 disabled:opacity-50 dark:border-violet-900/50 dark:bg-violet-500/10 dark:text-violet-300"
        >
          <FaEdit />
          Modifier
        </button>

        <button
          type="button"
          disabled={busy}
          onClick={onDelete}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900/50 dark:bg-rose-500/10 dark:text-rose-300"
          title="Supprimer"
        >
          <FaTrashAlt />
        </button>
      </div>
    </article>
  );
}

function MediaPreview({
  media,
}) {
  const [imageError, setImageError] =
    useState(false);

  const config =
    MEDIA_CONFIG[
      media.type_media
    ] || MEDIA_CONFIG.lien;

  if (
    media.type_media !== "image" ||
    imageError
  ) {
    return (
      <div
        className={`flex h-20 w-24 shrink-0 items-center justify-center rounded-2xl text-2xl ${config.color}`}
      >
        {config.icon}
      </div>
    );
  }

  const imageUrl =
    resolveAdminTutorialMediaUrl(
      media.miniature_url ||
        media.url
    );

  return (
    <div className="flex h-20 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 p-1 dark:border-slate-700 dark:bg-slate-800">
      <img
        src={imageUrl}
        alt={
          media.alt_text ||
          "Média du tutoriel"
        }
        onError={() =>
          setImageError(true)
        }
        className="h-full w-full rounded-xl object-contain"
      />
    </div>
  );
}